import { Injectable } from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';

import { BarChartDatePeriod } from '../models';

@Injectable({
  providedIn: 'root',
})
export class FormatService {
  constructor(
    private datePipe: DatePipe,
    private currencyPipe: CurrencyPipe,
    private decimalPipe: DecimalPipe,
  ) {}

  date(date: Date): string {
    return date ? this.datePipe.transform(date, 'shortDate') : undefined;
  }

  dateTime(date: Date): string {
    return date
      ? `${this.datePipe.transform(date, 'shortDate')} ${this.datePipe.transform(date, 'shortTime')}`
      : undefined;
  }

  dayOfWeekShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'EEE') : undefined;
  }

  dayOfWeekLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'EEEE') : undefined;
  }

  dayOfMonthShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'd') : undefined;
  }

  dayOfMonthLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'dd') : undefined;
  }

  monthShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'LLL') : undefined;
  }

  monthLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'LLLL') : undefined;
  }

  yearShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'yy') : undefined;
  }

  yearLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'yyyy') : undefined;
  }

  dateForPeriods(date: Date, period: BarChartDatePeriod): string {
    switch (period) {
      case BarChartDatePeriod.Week:
        return this.date(date);
      case BarChartDatePeriod.Month:
        return this.monthLong(date) + ' ' + this.yearLong(date);
      case BarChartDatePeriod.Year:
        return this.yearLong(date);
    }
  }

  dateItemsForPeriod(date: Date, period: BarChartDatePeriod): string {
    switch (period) {
      case BarChartDatePeriod.Week:
        return this.dayOfWeekShort(date);
      case BarChartDatePeriod.Month:
        return this.dayOfMonthShort(date);
      case BarChartDatePeriod.Year:
        return this.monthShort(date);
    }
  }

  number(value: number | string, decimalPlaces = 2): string {
    const valueNumber = this.parseNumberString(value);
    const roundValue = this.roundNumber(valueNumber, decimalPlaces);
    const digitsInfo = this.digitsInfo(decimalPlaces);
    return this.decimalPipe.transform(roundValue, digitsInfo);
  }

  currency(value: number, currencySymbol?: string, decimalPlaces: number = 2): string {
    const roundValue = this.roundNumber(value, decimalPlaces);
    const digitsInfo = this.digitsInfo(decimalPlaces);
    return this.currencyPipe.transform(roundValue, undefined, `${currencySymbol ?? ''} `, digitsInfo);
  }

  numberShort(value: number): number | string {
    if (value >= 1_000_000) {
      return `${value / 1_000_000}M`;
    } else if (value >= 1_000) {
      return `${value / 1_000}K`;
    }
    return value;
  }

  currencyShort(value: number, currencySymbol: string): string {
    return `${currencySymbol}${this.numberShort(value)}`;
  }

  multiLineToHtml(multiLine: string): string {
    return multiLine ? this.valuesToHtml(multiLine?.replace(/\r/g, '').split('\n')) : '';
  }

  valuesToHtml(values: string[]): string {
    return values.join('<br>');
  }

  roundNumber(value: number, decimalPlaces: number) {
    const roundNumber = Math.pow(10, decimalPlaces);
    return Math.round(value * roundNumber) / roundNumber;
  }

  parseNumberString(value: number | string): number {
    if (typeof value === 'string') {
      let digitSeparatorIndex = value.lastIndexOf('.');
      if (digitSeparatorIndex === -1) {
        digitSeparatorIndex = value.lastIndexOf(',');
      }
      if (digitSeparatorIndex !== -1) {
        const whole = value.slice(0, digitSeparatorIndex).replace(/[,\.]/g, '');
        const decimal = value.slice(digitSeparatorIndex + 1);
        return +`${whole}.${decimal}`;
      } else {
        return +value;
      }
    }

    return isNaN(value) ? 0 : value;
  }

  private digitsInfo(decimalPlaces: number) {
    return `1.${decimalPlaces}-${decimalPlaces}`;
  }
}
